import empty from "is-empty";
import moment from "moment-timezone";
import momentDurationFormatSetup from "moment-duration-format";

import * as Validators from "src/utils/validators";
import * as Formatters from "src/components/common/AgGrid/formatters";
import {
    HUMANIZED_TYPES,
    TX_STATES,
    LOAN_TYPES,
    OBJECTIVES,
    HUMANIZED_OBJECTIVES,
} from "src/redux/Tx/constants";
import { numberOrDash, intOrDash } from "src/utils/number";
import DatePickerEditor from "src/components/common/AgGrid/DatePicker";
import { setDateFormat } from "src/utils";
import {
    DEFAULT_CCY_OPTIONS,
    HUMANIZED_TX_TYPES,
    HUMANIZED_INTERMEDIARY,
} from "src/redux/Confirmations/constants";
import getHeaderValue from "src/components/common/AgGrid/headerGetter";
import {
    isFundNameValid,
    isListNameValid,
    TX_PURPOSE_SELECT_LABELS,
    TX_PURPOSE_SELECT_OPTIONS,
} from "src/utils/validators";
import { CurrencyFormatter } from "src/utils/CurrencyConversionService";
import { COUNTRY_OPTIONS } from "src/redux/MarketData/constants";
import { ROLE } from "src/redux/TopBar/constants";
import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { store } from "src/index";
import { TxSelectors } from "src/redux/Tx/selectors";
import DropdownItemRenderer from "src/components/common/AgGrid/DropdownItemRenderer";

import styles from "src/styles/agGridCustom.module.scss";
import { isEmpty } from "lodash";
import { CounterPartysSelectors } from "src/redux/CounterPartys/selectors";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";

momentDurationFormatSetup(moment);

const isRowInactive = (params) => {
    if (!params.data && params.node?.aggData) {
        for (let i = 0; i < params.node.childrenAfterFilter.length; i++) {
            const childRow = params.node.childrenAfterFilter[i];
            if (
                !Validators.isTxListRowInactive(
                    childRow.data.Type,
                    childRow.data.Status,
                    childRow.data.ReqQuantity
                )
            )
                return false;
        }
        return true;
    }
    return Validators.isTxListRowInactive(
        params.data?.Type,
        params.data?.Status,
        params.data?.ReqQuantity
    );
};

export const isRowSelectable = (params) => {
    const role = TopBarSelectors.Role(store.getState());
    const status = params.data?.Status;
    if (params.data?.isEAActionable) {
        return isTxHoldSelectable(
            params.data?.Type,
            status,
            role === ROLE.BORROWER ? "BORROWER" : "LENDER"
        );
    }

    if (role === ROLE.BORROWER) {
        return status === TX_STATES.OFFERED;
    } else {
        // can't accept needs with zero request quantity
        if (
            status === TX_STATES.PRE_LOAN_REQUESTED &&
            params.data?.ReqQuantity === 0
        )
            return false;

        return (
            status === TX_STATES.PRE_LOAN_REQUESTED ||
            status === TX_STATES.OFFERING
        );
    }
};

export const isRowSelectionDisabled = (params) => {
    const hasActionableEA = TxSelectors.hasActionableEA(store.getState());
    return (
        hasActionableEA !== null &&
        params.data?.isEAActionable !== hasActionableEA
    );
};

export const RowClassRules = {
    [styles.agRowInactive]: (params) => isRowInactive(params),
    [styles.agRowError]: (params) => !Validators.isTxListRowValid(params.data),
};

export const getRowClass = (params) => {
    return params.node?.key;
};

export const CommonColumnDefs = {
    sortable: true,
    tooltipComponent: "txTooltip",
    filter: true,
    resizable: true,
};

const isTxSelected = (id) => {
    const selectedRowIds = TxSelectors.SelectedRowIds(store.getState());
    return selectedRowIds.includes(id);
};

export const isFundNameEditable = (selectedRowIds, id, role) => {
    const isTxSelected = selectedRowIds?.includes(id);
    if (!isTxSelected) return false;
    if (role !== "BORROWER") return false;
    return isTxSelected;
};

export const isLoanTxEditable = (id, type, status, role) => {
    if (!isTxSelected(id)) return false;
    if (role !== "LENDER") return false;
    return [TX_STATES.PRE_LOAN_REQUESTED, TX_STATES.OFFERING].includes(status);
};

export const isTypeEditable = (id, status, role, isPortfolioTrade = false) => {
    if (!isTxSelected(id)) return false;
    if (role !== "LENDER") return false;
    if (isPortfolioTrade) return false;
    return [TX_STATES.PRE_LOAN_REQUESTED, TX_STATES.OFFERING].includes(status);
};

export const isObjectiveEditable = (id, status, role) => {
    if (!isTxSelected(id)) return false;
    if (role !== "LENDER") return false;
    return [TX_STATES.PRE_LOAN_REQUESTED, TX_STATES.OFFERING].includes(status);
};

export const isTxOfferRateEditable = (id, type, status, role) => {
    if (
        type === LOAN_TYPES.LOAN ||
        status === TX_STATES.PRE_LOAN_REQUESTED ||
        status === TX_STATES.OFFERING
    )
        return isLoanTxEditable(id, type, status, role);
    return (
        role === "LENDER" &&
        isTxHoldSelectable(type, status, role) &&
        isTxSelected(id)
    );
};

export const isTxOfferQtyEditable = (id, type, status, role) => {
    const isEAUpgradeRequest =
        isTxSelected(id) &&
        type === LOAN_TYPES.HOLD &&
        status === TX_STATES.UPGRADE_REQUESTED;

    return isEAUpgradeRequest || isLoanTxEditable(id, type, status, role);
};

export const isTxRequestRateEditable = (
    selectedRowIds,
    id,
    type,
    status,
    role
) => {
    const isTxSelected = selectedRowIds?.includes(id);
    return (
        role === "BORROWER" &&
        isTxHoldSelectable(type, status, role) &&
        isTxSelected &&
        status !== TX_STATES.FORKED
    );
};

export const isTxRequestQtyEditable = (
    selectedRowIds,
    id,
    type,
    status,
    role
) => {
    const isTxSelected = selectedRowIds?.includes(id);
    return (
        role === "BORROWER" &&
        isTxHoldSelectable(type, status, role) &&
        isTxSelected
    );
};

export const isTxPurposeEditable = (
    unsolicitedOffer,
    id,
    type,
    status,
    role
) => {
    return (
        role === "BORROWER" &&
        isTxSelected(id) &&
        unsolicitedOffer &&
        status === TX_STATES.OFFERED
    );
};

export const isMaturityEditable = (id) => {
    return isTxSelected(id);
};

export const isTxHoldSelectable = (type, status, role) => {
    const entry = { type, state: status };
    return TxSelectors.isTxHoldSelectable(store.getState(), entry, role);
};

export const isTxQtyComparable = (status, type, role) => {
    if (!status || role !== "LENDER") return false;
    if (type === LOAN_TYPES.HOLD && status !== TX_STATES.EXPIRED) return true;
    return (
        status !== TX_STATES.CONFIRMED &&
        status !== TX_STATES.EXPIRED &&
        status !== TX_STATES.PRE_LOAN_REQUESTED
    );
};
const isPartner = (params, role) => {
    const counterParties = CounterPartysSelectors.GetAllCptysById(
        store.getState()
    );
    const cptyValues = Object.values(counterParties);
    const cpty = cptyValues.find(
        (cpty) =>
            cpty.name ===
            (role === "lending" ? params?.data?.Borrower : params?.data?.Lender)
    );
    return cpty?.partner;
};
export const valueFormatter = (params, formatter = null) => {
    if (params.data?.ParentId && !params.data?.isPortfolioTrade) {
        return "";
    } else if (formatter && params.key) {
        const parent = TxSelectors.AggregatedTxEntries(store.getState()).find(
            (e) => e.id === params.key
        );

        return parent
            ? formatter({
                  ...params,
                  data: parent,
                  value: parent[params.colDef.field],
              })
            : "";
    }
    return formatter ? formatter(params) : params.value || "-";
};
export const AutoGroupColumnDef = {
    field: "Select",
    headerCheckboxSelectionFilteredOnly: true,
    headerCheckboxSelection: true,
    checkboxSelection: true,
    width: 35,
    pinned: "left",
    menuTabs: [],
    filter: false,
    sort: false,
    sortable: true,
    resizable: false,
    headerName: "",
    cellRendererParams: {
        suppressCount: true,
        checkbox: true, // This enables checkboxes on parent/group rows in tree data mode
    },
    cellClass: (params) => {
        if (params.data && params.data.Status === TX_STATES.OFFERING)
            return "grouped-cell-hide";
        return `grouped-cell-${params.value || params.data?.id}`;
    },
    cellClassRules: {
        [styles.disabledCheckbox]: (params) =>
            isRowSelectable(params) && isRowSelectionDisabled(params),
        [styles.hiddenCheckbox]: (params) =>
            isPortfolioConstituent(params.data),
    },
    valueFormatter: (params) => "",
};

const isPortfolioConstituent = (data) =>
    data?.isPortfolioTrade && !data?.isParent;

const usCollator = new Intl.Collator("en-US", {
    sensitivity: "base", // ignore case and accents
    ignorePunctuation: false,
    numeric: false,
});

export const getTxListColumnDefs = (isLender) => {
    const {
        "enable-purpose": enabledPurpose,
        "enable-min-increment-quantity": enableMinIncrementQty,
        "enable-etf-trades": enabledEtfTrades,
        // When limited-ui is on, the lender-side offer-composition cells
        // for fields OTHER than rate + qty must be readonly. Spec §2.1.3
        // and §2.2.2: under limited-ui, the lender may only adjust rate
        // and qty (DOWN only); "other trade modifications not allowed."
        // Backend enforces in TxController.validateLimitedUiOfferBounds
        // and createPreLoan; this is the user-facing defence-in-depth
        // that prevents the lender from even attempting an edit. Rate
        // and Qty stay editable so the lender can still adjust within
        // the allowed direction.
        "limited-ui": limitedUi,
    } = SettingsSelectors.FeatureFlags(store.getState());

    let columns = [
        isLender
            ? {
                  headerName: "Borrower",
                  field: "Borrower",
                  colId: "Details Borrower",
                  width: 120,
                  minWidth: 120,
                  flex: 15,
                  valueFormatter: (params) => {
                      if (isPortfolioConstituent(params.data)) {
                          return "";
                      }
                      return valueFormatter(params);
                  },
                  pinned: "left",
              }
            : {
                  headerName: "Lender",
                  field: "Lender",
                  colId: "Details Lender",
                  width: 120,
                  minWidth: 120,
                  flex: 15,
                  valueParser: (params) =>
                      params.value ? params.value?.join(", ") : "",
                  valueFormatter: (params) => {
                      if (isPortfolioConstituent(params.data)) {
                          return "";
                      }
                      return valueFormatter(params);
                  },
                  pinned: "left",
              },
        {
            headerName: "BBG Ticker",
            field: "BbgCode",
            colId: "Details BbgCode",
            width: 90,
            minWidth: 90,
            valueFormatter,
            pinned: "left",
        },
        {
            headerName: "Ticker",
            field: "Ticker",
            colId: "Details Ticker",
            width: 80,
            minWidth: 80,
            valueFormatter,
            pinned: "left",
        },
        {
            headerName: "ISIN",
            field: "ISIN",
            colId: "Details ISIN",
            width: 120,
            minWidth: 120,
            valueFormatter,
            pinned: "left",
        },
        {
            headerName: "Removed",
            field: "isRemovedConstituent",
            colId: "SortRemoved",
            sortable: true,
            sort: "desc",
            sortIndex: 0,
            hide: true,
        },
        {
            headerName: "Security Name",
            field: "Name",
            colId: "Details Security Name",
            width: 180,
            minWidth: 180,
            valueFormatter,
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params?.data?.Name && params.data.Name === "Pending",
            },
            comparator: (a, b) => usCollator.compare(a ?? "", b ?? ""),
            pinned: "left",
        },
        enabledEtfTrades
            ? {
                  headerName: "Constituents",
                  field: "isPortfolioTrade",
                  colId: "Details Constituents",
                  width: 100,
                  minWidth: 100,
                  pinned: "left",
                  cellRenderer: "constituentsRenderer",
              }
            : {},
        {
            headerName: "ID",
            field: "id",
            colId: "Details Id",
            width: 150,
            minWidth: 150,
            flex: 15,
            cellClass: [],
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params?.data?.id && params.data.id.startsWith("temp"),
            },
        },
        isLender
            ? {
                  headerName: "Split",
                  field: "splitOffer",
                  colId: "Details Split",
                  cellRenderer: "splitOffer",
                  minWidth: 80,
                  flex: 8,
                  headerClass: [styles.agHeaderLeftAlign],
                  cellClass: [],
                  menuTabs: [],
                  filter: false,
                  sortable: false,
                  resizable: false,
                  tooltipValueGetter: (params) => ({ value: params.value }),
                  tooltipComponentParams: { type: "txSplitOffer" },
              }
            : {},
        isLender
            ? {
                  headerName: "List Name",
                  field: "ListName",
                  colId: "Details List Name",
                  // List Name stays editable under limited-ui — the
                  // lender must pick which of their lists to fulfill
                  // an incoming locate from. The spec's
                  // "other modifications disabled" (§2.1.3 / §2.2.2)
                  // covers things like maturity / collateral type that
                  // change the trade's economics, not the source-of-
                  // inventory pointer. Removing the earlier `!limitedUi`
                  // gate here that left the column read-only.
                  editable: (params) =>
                      !isPortfolioConstituent(params.data) &&
                      isLoanTxEditable(
                          params.data?.id,
                          params.data?.Type,
                          params.data?.Status,
                          params.data?.MyRole
                      ),
                  width: 110,
                  minWidth: 110,
                  flex: 11,
                  cellClass: [],
                  cellClassRules: {
                      [styles.agEditable]: (params) =>
                          params.colDef.editable(params),
                      [styles.agCellError]: (params) =>
                          params.colDef.editable(params) &&
                          !isListNameValid(params.value),
                  },
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => {
                      const usesClientPool = AuthSelectors.UsesClientPool(
                          store.getState()
                      );

                      const listNames = usesClientPool
                          ? SettingsSelectors.SelectableListNamesForBrokerCpty(
                                store.getState(),
                                params.data?.BbgCode
                            )
                          : params.context.listNames;

                      return {
                          values: listNames,
                          formatValue: (value) => (empty(value) ? "-" : value),
                          cellRenderer: DropdownItemRenderer,
                      };
                  },
                  valueGetter: (params) =>
                      isPortfolioConstituent(params.data)
                          ? params.node?.parent?.data?.ListName
                          : params.data?.ListName,
                  valueFormatter: (params) => params.value || "-",
              }
            : {},
        isLender && enableMinIncrementQty
            ? {
                  headerName: "Minimum\nQty",
                  field: "MinQuantity",
                  colId: "Details Minimum Qty",
                  width: 100,
                  minWidth: 100,
                  flex: 10,
                  headerClass: [styles.agHeaderPre, styles.agHeaderRightAlign],
                  cellClass: [styles.agRightAlign],
                  valueParser: (params) =>
                      Validators.parseIntToSizeLimit(params.newValue),
                  valueFormatter: (params) =>
                      valueFormatter(params, (params) =>
                          Formatters.formatQuantity(
                              params.value,
                              params.data?.Country,
                              false
                          )
                      ),
                  tooltipValueGetter: (params) => ({ value: params.value }),
                  tooltipComponentParams: { type: "txListQty" },
              }
            : {},
        isLender && enableMinIncrementQty
            ? {
                  headerName: "Increment\nQty",
                  field: "IncQuantity",
                  colId: "Details Increment Qty",
                  width: 100,
                  minWidth: 100,
                  flex: 10,
                  headerClass: [styles.agHeaderPre, styles.agHeaderRightAlign],
                  cellClass: [styles.agRightAlign],
                  valueParser: (params) =>
                      Validators.parseIntToSizeLimit(params.newValue),
                  valueFormatter: (params) =>
                      valueFormatter(params, (params) =>
                          Formatters.formatQuantity(
                              params.value,
                              params.data?.Country,
                              false
                          )
                      ),
                  tooltipValueGetter: (params) => ({ value: params.value }),
                  tooltipComponentParams: { type: "txListQty" },
              }
            : {},
        {
            headerValueGetter: (params) =>
                params.context.role === ROLE.BORROWER
                    ? "Sent\nQty"
                    : "Received\nQty",
            headerName: "Received\nQty",
            field: "ReqQuantity",
            colId: "Details Request Qty",
            width: 100,
            minWidth: 100,
            flex: 10,
            headerClass: [styles.agHeaderPre, styles.agHeaderRightAlign],
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.textBlue]: (params) =>
                    isTxQtyComparable(
                        params.data?.Status,
                        params.data?.Type,
                        params.data?.MyRole
                    ) && params.value <= params.data?.RemainingQuantity,
                [styles.textRed]: (params) =>
                    isTxQtyComparable(
                        params.data?.Status,
                        params.data?.Type,
                        params.data?.MyRole
                    ) && params.value > params.data?.RemainingQuantity,
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) =>
                    params.colDef.editable(params) &&
                    !Validators.isTxQuantityValid(
                        params.data?.Country,
                        params.data?.Type,
                        params.data?.MyRole,
                        params.value,
                        params.data?.OfferQuantity,
                        params.data?.Status,
                        // NB: pre-existing call passes only 6 args
                        // (Status lands in the minQty slot — unrelated
                        // latent bug); explicit `false` ensures
                        // limitedUi is plumbed even though this is the
                        // borrower's Tru-Qty column where the lender
                        // clamp doesn't apply anyway.
                        undefined,  // incQty
                        undefined,  // enableMinIncrementQtyChecks
                        undefined,  // isPartnerTrade
                        undefined,  // status (placeholder; latent bug above)
                        false,      // limitedUi — N/A for borrower's req qty
                    ),
            },
            editable: (params) =>
                !isPortfolioConstituent(params.data) &&
                isTxRequestQtyEditable(
                    params.context.selectedRowIds,
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            valueParser: (params) =>
                Validators.parseIntToSizeLimit(params.newValue),
            valueFormatter: (params) => {
                let displayValue = params.value;

                const formattedQty = valueFormatter(params, (params) =>
                    Formatters.formatQuantity(
                        displayValue,
                        params.data?.Country,
                        false
                    )
                );

                if (params.data?.isParent) {
                    return `${formattedQty} CU`;
                }
                return formattedQty;
            },
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "txListQty" },
        },
        {
            headerName: "Response\nQty",
            field: "OfferQuantity",
            colId: "Details Offer Qty",
            width: 100,
            minWidth: 100,
            flex: 10,
            headerClass: [styles.agHeaderPre, styles.agHeaderRightAlign],
            editable: (params) =>
                !isPortfolioConstituent(params.data) &&
                isTxOfferQtyEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellWarning]: (params) => {
                    if (!params.colDef.editable(params)) return false;

                    const {
                        "enable-min-increment-quantity": enableMinIncrementQty,
                    } = SettingsSelectors.FeatureFlags(store.getState());

                    if (
                        !Validators.isTxQuantityValid(
                            params.data?.Country,
                            params.data?.Type,
                            params.data?.MyRole,
                            null,
                            params.value,
                            params.data?.MinQuantity,
                            params.data?.IncQuantity,
                            enableMinIncrementQty,
                            undefined,  // isPartnerTrade
                            params.data?.Status,
                            // reqQty=null → limited-ui clamp can't
                            // fire (isQuantityValid(null) is false);
                            // pass `false` explicitly so the no-default
                            // signature isn't accidentally satisfied
                            // by undefined.
                            false,
                        )
                    )
                        return false; // covered by agCellError

                    // highlight cell for offers which exceed non-dynamic list inventory qty
                    return (
                        !empty(params.data?.ListName) &&
                        !Validators.isDynamicOrClientPoolList(
                            params.data?.ListName
                        ) &&
                        !Validators.isSelectedRowsTxQuantityBelowInventoryQty(
                            store.getState(),
                            params.data?.ListName,
                            false,
                            params.data?.SecurityId,
                            params.context.role
                        )
                    );
                },
                [styles.agCellError]: (params) => {
                    if (!params.colDef.editable(params)) return false;

                    const {
                        "enable-min-increment-quantity": enableMinIncrementQty,
                        // Spec §2.1.3.3 — limited-ui caps lender's
                        // offered qty at borrower's requested qty.
                        // Cell-level (red) validator must agree with
                        // the row-level (orange) and submit-button
                        // validators; thread the flag through.
                        "limited-ui": limitedUi,
                    } = SettingsSelectors.FeatureFlags(store.getState());

                    let ParentId = params.data?.ParentId;
                    let offerQuantity =
                        ParentId === undefined
                            ? params.value
                            : TxSelectors.TotalOfferForPreloanId(
                                  store.getState(),
                                  ParentId
                              );
                    if (
                        !Validators.isTxQuantityValid(
                            params.data?.Country,
                            params.data?.Type,
                            params.data?.MyRole,
                            params.data?.ReqQuantity,
                            offerQuantity,
                            params.data?.MinQuantity,
                            params.data?.IncQuantity,
                            enableMinIncrementQty,
                            params.data?.IsPartnerTrade,
                            params.data?.Status,
                            limitedUi
                        )
                    )
                        return true;

                    if (
                        params.data.isPortfolioTrade &&
                        offerQuantity > params.data.ReqQuantity
                    )
                        return true;

                    return (
                        Validators.isDynamicOrClientPoolList(
                            params.data?.ListName
                        ) &&
                        !Validators.isSelectedRowsTxQuantityBelowInventoryQty(
                            store.getState(),
                            params.data?.ListName,
                            true,
                            params.data?.SecurityId,
                            params.context.role
                        )
                    );
                },
            },
            valueParser: (params) =>
                Validators.parseIntToSizeLimit(params.newValue),
            valueGetter: (params) => {
                let cellValue = params.data?.OfferQuantity;
                if (isPortfolioConstituent(params.data)) {
                    // Note: this will not work well for decimals, we'll need to add the cu_value
                    // to the Security level on the backend.
                    const cu_value =
                        params.data.ReqQuantity /
                        params.node?.parent?.data?.ReqQuantity;
                    const offerQty = params.node?.parent?.data?.OfferQuantity;
                    cellValue = offerQty * cu_value;
                }
                return cellValue;
            },
            valueFormatter: (params) => {
                const formattedQty = Formatters.formatQuantity(
                    params.value,
                    params.data?.Country,
                    params.colDef.editable(params)
                );

                if (params.data?.isParent && formattedQty !== "-") {
                    return `${formattedQty} CU`;
                }
                return formattedQty;
            },
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "txListQty" },
        },
        isLender
            ? {
                  headerName: "My Qty",
                  field: "UploadedQuantity",
                  colId: "Details My Quantity",
                  width: 80,
                  minWidth: 80,
                  flex: 8,
                  headerClass: [styles.agInventoryHeader, styles.agHeaderRightAlign],
                  cellClass: [styles.agRightAlign],
                  valueFormatter: (params) =>
                      intOrDash({ value: params.value, replacement: "" }),
              }
            : {},
        isLender
            ? {
                  headerName: "Net Qty",
                  field: "RemainingQuantity",
                  colId: "Details Tru Quantity",
                  width: 80,
                  minWidth: 80,
                  flex: 8,
                  headerClass: [styles.agTruHeader, styles.agHeaderRightAlign],
                  cellClass: [styles.agRightAlign],
                  valueFormatter: (params) =>
                      intOrDash({ value: params.value, replacement: "" }),
                  cellClassRules: {
                      [styles.agCellWarning]: (params) => {
                          if (!isTxSelected(params.data?.id)) return false; // don't highlight non-selected rows

                          // highlight cell for offers which exceed non-dynamic list inventory qty
                          return (
                              !empty(params.data?.ListName) &&
                              !Validators.isDynamicOrClientPoolList(
                                  params.data?.ListName
                              ) &&
                              !Validators.isSelectedRowsTxQuantityBelowInventoryQty(
                                  store.getState(),
                                  params.data?.ListName,
                                  false,
                                  params.data?.SecurityId,
                                  params.context.role
                              )
                          );
                      },
                  },
                  tooltipValueGetter: (params) => ({
                      value: params.data?.OfferQuantity,
                  }),
                  tooltipComponentParams: { type: "txListTruQty" },
              }
            : {},
        isLender
            ? {
                  headerName: "Net \nUtilization (%)",
                  field: "Utilization",
                  colId: "Details Utilization",
                  width: 120,
                  minWidth: 120,
                  flex: 12,
                  headerClass: [
                      styles.agTruHeader,
                      styles.agHeaderPre,
                      styles.agHeaderRightAlign,
                  ],
                  cellClass: [styles.agRightAlign],
                  valueFormatter: (params) =>
                      numberOrDash({ value: params.value, replacement: "" }),
              }
            : {},
        {
            headerValueGetter: (params) =>
                params.context.role === ROLE.BORROWER
                    ? "Sent\nRate (%)"
                    : "Received\nRate (%)",
            headerName: "Received\nRate (%)",
            field: "ReqRate",
            colId: "Details Request Rate",
            width: 80,
            minWidth: 80,
            flex: 8,
            headerClass: [styles.agHeaderPre, styles.agHeaderRightAlign],
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.textBlue]: (params) =>
                    isTxQtyComparable(
                        params.data?.Status,
                        params.data?.Type,
                        params.data?.MyRole
                    ) && params.value >= params.data?.PreferredRate,
                [styles.textRed]: (params) =>
                    isTxQtyComparable(
                        params.data?.Status,
                        params.data?.Type,
                        params.data?.MyRole
                    ) && params.value < params.data?.PreferredRate,
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) =>
                    params.colDef.editable(params) &&
                    !Validators.isTxRateValid(
                        params.data?.Type,
                        params.data?.MyRole,
                        params.value,
                        params.data?.OfferRate,
                        // Borrower's Request Rate column — the lender
                        // limited-ui clamp doesn't apply here. Explicit
                        // false so the no-default signature is honored.
                        false,
                    ),
            },
            editable: (params) =>
                !isPortfolioConstituent(params.data) &&
                isTxRequestRateEditable(
                    params.context.selectedRowIds,
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            valueParser: (params) =>
                Validators.parseFloatToSizeLimit(params.newValue),
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.ReqRate
                    : params.data?.ReqRate,
            valueFormatter: (params) =>
                valueFormatter(params, (params) =>
                    Formatters.formatRate(params.value, false)
                ),
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "txListRate" },
        },
        {
            headerName: "Response\nRate",
            field: "OfferRate",
            colId: "Details Offer Rate",
            width: 80,
            minWidth: 80,
            flex: 8,
            headerClass: [styles.agHeaderPre, styles.agHeaderRightAlign],
            editable: (params) =>
                !isPortfolioConstituent(params.data) &&
                isTxOfferRateEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.textBlue]: (params) =>
                    isTxQtyComparable(
                        params.data?.Status,
                        params.data?.Type,
                        params.data?.MyRole
                    ) && params.value >= params.data?.PreferredRate,
                [styles.textRed]: (params) =>
                    isTxQtyComparable(
                        params.data?.Status,
                        params.data?.Type,
                        params.data?.MyRole
                    ) && params.value < params.data?.PreferredRate,
                [styles.agCellError]: (params) => {
                    if (!params.colDef.editable(params)) return false;

                    let collateralType = params.data?.CollateralType;
                    let requestRate =
                        params.data?.OriginValue?.OriginRequestRate;

                    if (params.data.IsPartnerTrade) {
                        return !Validators.isPartnerRateCompatible(
                            collateralType,
                            requestRate,
                            params.value
                        );
                    } else {
                        // Spec §2.1.3.2 — limited-ui caps lender's
                        // offer rate at borrower's requested rate (if
                        // borrower specified one). Pass the real
                        // requestRate (not null) + the limitedUi flag
                        // so this cell-level check matches the row /
                        // submit-button validators.
                        const { "limited-ui": limitedUi } =
                            SettingsSelectors.FeatureFlags(store.getState());
                        return !Validators.isTxRateValid(
                            params.data?.Type,
                            params.data?.MyRole,
                            requestRate,
                            params.value,
                            limitedUi
                        );
                    }
                },
            },
            valueParser: (params) =>
                Validators.parseFloatToSizeLimit(params.newValue),
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.OfferRate
                    : params.data?.OfferRate,
            valueFormatter: (params) =>
                Formatters.formatRate(
                    params.value,
                    params.colDef.editable(params)
                ),
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "txListRate" },
        },
        isLender
            ? {
                  headerName: "My Rate (%)",
                  field: "PreferredRate",
                  colId: "Details Preferred Rate",
                  width: 80,
                  minWidth: 80,
                  flex: 8,
                  headerClass: [styles.agInventoryHeader, styles.agHeaderRightAlign],
                  cellClass: [styles.agRightAlign],
                  valueFormatter: (params) =>
                      numberOrDash({ value: params.value, replacement: "" }),
              }
            : {},
        {
            headerName: "F/R",
            field: "FR",
            colId: "Details FR",
            width: 60,
            minWidth: 60,
            flex: 6,
            cellClass: [],
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.FR
                    : params.data?.FR,
            valueFormatter: (params) => params.value || "-",
        },
        {
            headerName: "V/D",
            field: "ValueDate",
            colId: "Details Value Date",
            width: 90,
            minWidth: 90,
            flex: 9,
            filter: false,
            // Value Date stays editable under limited-ui per spec
            // §2.1.3.1: "Must fill in any missing required fields
            // (e.g. List Name, Rate, Value Date)". V/D is one of
            // the cells the lender legitimately needs to fill when
            // responding to an incoming locate — it's NOT in the
            // §2.1.3.4 "other modifications not allowed" bucket.
            editable: (params) =>
                !isPortfolioConstituent(params.data) &&
                isLoanTxEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellEditor: DatePickerEditor,
            cellEditorPopup: true,
            cellClass: [],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) => {
                    if (!params.data) return false;
                    if (!params.colDef.editable(params)) return false;

                    let isValueDateValidForPartnerTrade =
                        !params.data?.IsPartnerTrade ||
                        params.data?.OriginValue?.ValueDate === undefined ||
                        params.value === params.data?.OriginValue?.ValueDate;

                    return (
                        !Validators.isDateValid(
                            params.value,
                            params.data?.TradeableDates
                        ) || !isValueDateValidForPartnerTrade
                    );
                },
            },
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.ValueDate
                    : params.data?.ValueDate,
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "ValueDate", checkOrigin: true },
        },
        {
            headerName: "Records Type",
            field: "NewType",
            colId: "Details Type",
            width: 80,
            minWidth: 80,
            flex: 8,
            cellClass: [],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
            },
            valueFormatter: (params) => HUMANIZED_TYPES[params.value] || "-",
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isTypeEditable(
                    params.data?.id,
                    params.data?.Status,
                    params.data?.MyRole,
                    params.data?.isPortfolioTrade
                ),
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: (params) => ({
                values: isPartner(params, params?.context?.role)
                    ? [LOAN_TYPES.LOAN]
                    : [LOAN_TYPES.LOAN, LOAN_TYPES.HOLD],
                formatValue: (value) => HUMANIZED_TYPES[value],
                cellRenderer: DropdownItemRenderer,
            }),
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "loanType", checkOrigin: true },
            filterParams: {
                valueFormatter: (params) =>
                    HUMANIZED_TYPES[params.value] || "-",
            },
        },
        {
            headerName: "Status",
            field: "StatusName",
            colId: "Details Status",
            width: 130,
            minWidth: 130,
            flex: 13,
            valueFormatter: (params) => {
                if (params.node.level === 1) {
                    // split offer child row
                    const statuses = [
                        ...new Set(
                            params.node?.parent.childrenAfterFilter.map(
                                (c) => c.data.Status
                            )
                        ),
                    ];
                    if (
                        statuses.length < 2 &&
                        statuses[0] === TX_STATES.OFFERING
                    )
                        return "";
                } else if (!params.data) {
                    // split offer parent row
                    const node = params.node.childrenAfterFilter.find(
                        (c) => c.data.Status === TX_STATES.OFFERED
                    );
                    if (node) {
                        return Validators.translateStatus(node.data.Status);
                    }
                    if (!params.value) return "Done";
                }
                return params.value;
            },
        },
        {
            headerName: "Time Left",
            field: "TimeLeft",
            colId: "Details Countdown",
            width: 80,
            minWidth: 80,
            flex: 8,
            headerClass: [],
            cellClass: [],
            valueFormatter: (params) => {
                if (params.data?.ExpiresAt) {
                    const now = Date.now();
                    const expiresAt = new Date(
                        params.data.ExpiresAt * 1000
                    ).getTime();
                    if (expiresAt > now) {
                        let gap = expiresAt - now;
                        return moment
                            .duration(moment.utc(gap))
                            .format(gap >= 60 * 1000 ? "hh:mm:ss" : "00:ss");
                    }
                }
                return params.value || "-";
            },
        },
        {
            headerName: "Last Updated",
            field: "LastUpdated",
            colId: "Details Last Updated",
            width: 110,
            minWidth: 110,
            flex: 11,
            headerClass: [],
            valueFormatter: (params) =>
                // NOTE: Java UTC Instant => JS Local Time
                // should not convert date object to string in selector
                // because the sorting won't work as expected
                params.value
                    ? new Date(params.value * 1000).toLocaleTimeString(
                          "en-US",
                          {
                              hour12: false,
                          }
                      )
                    : null,
        },
        !isLender
            ? {
                  headerName: "Fund Name",
                  field: "FundName",
                  colId: "Details Fund Name",
                  editable: (params) =>
                      !isPortfolioConstituent(params.data) &&
                      isFundNameEditable(
                          params.context.selectedRowIds,
                          params.data?.id,
                          params.data?.MyRole
                      ),
                  width: 110,
                  minWidth: 110,
                  cellClass: [],
                  valueGetter: (params) =>
                      isPortfolioConstituent(params.data)
                          ? params.node?.parent?.data?.FundName
                          : params.data?.FundName,
                  valueFormatter: (params) => params.value || "-",
                  cellClassRules: {
                      [styles.agEditable]: (params) =>
                          params.colDef.editable(params),
                      [styles.agCellError]: (params) =>
                          params.colDef.editable(params) &&
                          !isFundNameValid(params.value),
                  },
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => ({
                      values: params.context.fundNames,
                      formatValue: (value) => (empty(value) ? "-" : value),
                      cellRenderer: DropdownItemRenderer,
                  }),
              }
            : {},
        {
            headerName: "Callable",
            field: "Callable",
            colId: "Details Callable",
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isLoanTxEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            width: 80,
            minWidth: 80,
            flex: 8,
            cellClass: [],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) => {
                    if (!params.colDef.editable(params)) return false;

                    if (!params.data?.IsPartnerTrade) return false;

                    let requestCallable = params.data?.OriginValue?.Callable;

                    return params.value !== requestCallable;
                },
            },
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.Callable
                    : params.data?.Callable,
            valueFormatter: (params) =>
                empty(params.value) ? "-" : params.value ? "Y" : "N",

            cellEditorParams: {
                values: [true, false],
                formatValue: (value) => (value ? "Y" : "N"),
                cellRenderer: DropdownItemRenderer,
            },
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "Callable", checkOrigin: true },
        },
        {
            headerName: "Div(%)",
            field: "Dividend",
            colId: "Details Dividend",
            width: 70,
            minWidth: 70,
            flex: 7,
            headerClass: [styles.agHeaderRightAlign],
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isLoanTxEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) => {
                    if (!params.colDef.editable(params)) return false;

                    let requestDividendRate =
                        params.data?.OriginValue?.Dividend;
                    let requestDividendIndicator =
                        params.data?.OriginValue?.DividendIndicator;

                    return (
                        !Validators.isDividendValid(params.value) ||
                        (params.data.IsPartnerTrade &&
                            !Validators.isDividendCompatible(
                                requestDividendIndicator,
                                requestDividendRate,
                                params.value
                            ))
                    );
                },
            },
            valueParser: (params) =>
                Validators.parseFloatToSizeLimit(params.newValue),
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.Dividend
                    : params.data?.Dividend,
            valueFormatter: (params) =>
                Formatters.formatDiv(
                    params.value,
                    params.colDef.editable(params)
                ),
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "Dividend", checkOrigin: true },
        },
        {
            headerName: "Comment",
            field: "Comment",
            colId: "Details Comment",
            width: 120,
            minWidth: 120,
            flex: 12,
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.Comment
                    : params.data?.Comment,
            valueFormatter: (params) => params.value || "-",
        },
        {
            headerName: "Coll Type",
            field: "CollateralType",
            colId: "Details Coll Type",
            width: 60,
            minWidth: 60,
            flex: 6,
            headerClass: [],
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isLoanTxEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellClass: [],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) =>
                    params.colDef.editable(params) &&
                    (!Validators.isCollTypeValid(params.value) ||
                        !Validators.isOfferCollTypeCompatibleWithRequestCollType(
                            params.data?.IsPartnerTrade,
                            params.data?.CollateralType,
                            params.data?.OriginValue?.CollateralType
                        )),
            },
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: ["CA", "NC", "CP"],
                formatValue: (value) => (empty(value) ? "-" : value),
                cellRenderer: DropdownItemRenderer,
            },
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.CollateralType
                    : params.data?.CollateralType,
            valueFormatter: (params) => params.value || "-",
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: {
                type: "CollateralType",
                checkOrigin: true,
            },
        },
        {
            headerName: "Coll CCY",
            field: "CollateralCCY",
            colId: "Details Coll Ccy",
            width: 60,
            minWidth: 60,
            flex: 6,
            headerClass: [],
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isLoanTxEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellClass: [],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) =>
                    (params.colDef.editable(params) &&
                        !Validators.isCollCcyValid(
                            params.value,
                            params.data?.SelectableCCY
                        )) ||
                    !Validators.isOfferCollCcyCompatibleWithRequestCollCcy(
                        params.data?.IsPartnerTrade,
                        params.data?.CollateralCCY,
                        params.data?.OriginValue?.CollateralCCY
                    ),
            },
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: (params) => ({
                values: params.data?.SelectableCCY || DEFAULT_CCY_OPTIONS,
                formatValue: (value) => (empty(value) ? "-" : value),
                cellRenderer: DropdownItemRenderer,
            }),
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.CollateralCCY
                    : params.data?.CollateralCCY,
            valueFormatter: (params) => params.value || "-",
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: {
                type: "CollateralCCY",
                checkOrigin: true,
            },
        },
        {
            headerName: "Maturity",
            field: "Maturity",
            colId: "Details Maturity",
            width: 90,
            minWidth: 90,
            flex: 9,
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isMaturityEditable(params.data?.id),
            cellEditor: DatePickerEditor,
            cellEditorPopup: true,
            cellClass: [],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) => {
                    if (!params.data) return false;
                    if (!params.colDef.editable(params)) return false;

                    let isMaturityDateValidForPartnerTrade =
                        !params.data?.IsPartnerTrade ||
                        (params.data?.OriginValue?.Maturity || "9999-12-31") ===
                            (params.value || "9999-12-31");

                    return (
                        !Validators.isTermValid(params.data.Term) ||
                        !isMaturityDateValidForPartnerTrade
                    );
                },
            },
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.Maturity
                    : params.data?.Maturity,
            valueFormatter: (params) => setDateFormat(params.value, "/") || "-",
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "Maturity", checkOrigin: true },
        },
        {
            headerName: "Days to Maturity",
            field: "Term",
            colId: "Details Days to Maturity",
            minWidth: 100,
            flex: 10,
            headerClass: [styles.agHeaderRightAlign],
            cellClass: [styles.agRightAlign],
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.Term
                    : params.data?.Term,
            valueFormatter: (params) =>
                params.value ? `${params.value} days` : "-",
        },
        !isLender && enabledPurpose
            ? {
                  headerName: "Purpose",
                  field: "Purpose",
                  colId: "Details Purpose",
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  width: 100,
                  editable: (params) => {
                      if (!params.data) return false;

                      return (
                          !isPortfolioConstituent(params.data) &&
                          isTxPurposeEditable(
                              params.data.unsolicitedOffer,
                              params.data.id,
                              params.data.Type,
                              params.data.Status,
                              params.data.MyRole
                          )
                      );
                  },
                  cellClassRules: {
                      [styles.agEditable]: (params) =>
                          params.colDef.editable(params),
                  },
                  cellEditorParams: () => ({
                      values: TX_PURPOSE_SELECT_OPTIONS(),
                      formatValue: TX_PURPOSE_SELECT_LABELS,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueFormatter: (params) => {
                      return TX_PURPOSE_SELECT_LABELS(params.value);
                  },
              }
            : {},
        {
            headerName: "Intermediary",
            field: "Intermediary",
            colId: "Details Intermediary",
            width: 150,
            minWidth: 150,
            flex: 15,
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isLoanTxEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: (params) => ({
                values: Validators.COMPATIBLE_INTERMEDIARY_VALUES(
                    params.data.TxType
                ),
                formatValue: (value) =>
                    HUMANIZED_INTERMEDIARY[value] || HUMANIZED_INTERMEDIARY.ANY,
                cellRenderer: DropdownItemRenderer,
            }),
            cellClass: [],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
            },
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "Intermediary", checkOrigin: true },
            filterParams: {
                valueFormatter: (params) =>
                    HUMANIZED_INTERMEDIARY[params.value] || "-",
            },
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.Intermediary
                    : params.data?.Intermediary,
            valueFormatter: (params) =>
                HUMANIZED_INTERMEDIARY[params.value] || "-",
        },
        {
            headerName: "Type",
            field: "TxType",
            colId: "Details TX Type",
            width: 92,
            minWidth: 92,
            flex: 9,
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isLoanTxEditable(
                    params.data?.id,
                    params.data?.Type,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: (params) => ({
                values: Validators.COMPATIBLE_TX_TYPE_VALUES(
                    params.data.Intermediary
                ),
                formatValue: (value) =>
                    HUMANIZED_TX_TYPES[value] || HUMANIZED_TX_TYPES.ANY,
                cellRenderer: DropdownItemRenderer,
            }),
            cellClass: [],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) => {
                    return (
                        params.colDef.editable(params) &&
                        !Validators.isCompatibleIntermediary(
                            params.value,
                            params.data.Intermediary
                        )
                    );
                },
            },
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "TxType", checkOrigin: true },
            filterParams: {
                valueFormatter: (params) =>
                    HUMANIZED_TX_TYPES[params.value] || "-",
            },
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.TxType
                    : params.data?.TxType,
            valueFormatter: (params) => HUMANIZED_TX_TYPES[params.value] || "-",
        },
        {
            headerName: "Objective",
            field: "Objective",
            colId: "Details Objective",
            width: 100,
            minWidth: 100,
            flex: 8,
            cellClass: [styles.agLeftAlign],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
            },
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.node?.parent?.data?.Objective
                    : params.data?.Objective,
            valueFormatter: (params) =>
                HUMANIZED_OBJECTIVES[params.value] ||
                params.value || // should handle a free text value which is legal on Api
                "-",
            editable: (params) =>
                !limitedUi &&
                !isPortfolioConstituent(params.data) &&
                isObjectiveEditable(
                    params.data?.id,
                    params.data?.Status,
                    params.data?.MyRole
                ),
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: [
                    "",
                    OBJECTIVES.REGULAR,
                    OBJECTIVES.LP,
                    OBJECTIVES.FINANCING,
                ],
                formatValue: (value) => HUMANIZED_OBJECTIVES[value] || "-",
                cellRenderer: DropdownItemRenderer,
            },
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "objective", checkOrigin: true },
            filterParams: {
                valueFormatter: (params) =>
                    HUMANIZED_OBJECTIVES[params.value] || "-",
            },
        },
        {
            headerValueGetter: (params) => getHeaderValue(params),
            headerName: "Value (Sent)",
            field: "ReqAmount",
            colId: "Details Request Amount",
            width: 110,
            minWidth: 110,
            flex: 11,
            cellClass: [styles.agRightAlign],
            headerClass: [styles.agHeaderRightAlign],
            valueGetter: (params) =>
                isPortfolioConstituent(params.data)
                    ? params.data?.ReqQuantity * params.data?.PreviousClose
                    : params.data?.ReqAmount,
            valueFormatter: (params) =>
                valueFormatter(params, (params) =>
                    intOrDash({ value: params.value })
                ),
        },
        {
            headerValueGetter: (params) => getHeaderValue(params),
            headerName: "Value (Response)",
            field: "OfferAmount",
            colId: "Details Offer Amount",
            width: 110,
            minWidth: 110,
            flex: 11,
            cellClass: [styles.agRightAlign],
            headerClass: [styles.agHeaderRightAlign],
            valueGetter: (params) => {
                let cellValue = params.data?.OfferAmount;
                if (isPortfolioConstituent(params.data)) {
                    const previousClose = params.data?.PreviousClose;

                    const parentReqQty =
                        params.node?.parent?.data?.ReqQuantity ?? 1;
                    const parentOfferQty =
                        params.node?.parent?.data?.OfferQuantity ?? 1;

                    const cu_value = params.data?.ReqQuantity / parentReqQty;

                    cellValue = parentOfferQty * cu_value * previousClose;
                }
                return cellValue;
            },
            valueFormatter: (params) => intOrDash({ value: params.value }),
        },
        {
            headerValueGetter: (params) => getHeaderValue(params),
            headerName: "Prev Close",
            field: "PreviousClose",
            colId: "Details Prev Close",
            width: 100,
            minWidth: 100,
            flex: 10,
            headerClass: [styles.agHeaderPre, styles.agHeaderRightAlign],
            cellClass: [styles.agRightAlign],
            valueFormatter: (params) =>
                valueFormatter(params, (params) =>
                    params.value
                        ? CurrencyFormatter.format(
                              params.value,
                              params.context.preferredCcy ||
                                  COUNTRY_OPTIONS[
                                      params.data?.Country ||
                                          params.node.aggData["Details Country"]
                                  ]?.currency
                          )
                        : "-"
                ),
        },
        {
            headerName: "Country",
            field: "Country",
            colId: "Details Country",
            width: 80,
            minWidth: 80,
            flex: 8,
            cellClass: [],
            valueFormatter: (params) =>
                valueFormatter(params, (params) => params.value || "-"),
        },
        {
            headerName: "User",
            field: "User",
            colId: "Details User",
            width: 90,
            minWidth: 90,
            flex: 9,
        },
        {
            headerName: "Local Name",
            field: "LocalName",
            colId: "Details Local Name",
            width: 150,
            minWidth: 150,
            valueFormatter,
        },
        {
            headerName: "Ref ID",
            field: "OrderId",
            colId: "Details Order ID",
            width: 80,
            minWidth: 80,
            flex: 8,
            cellClass: [],
            hide: true,
            valueFormatter: (params) => params.value || "-",
        },
    ];
    return columns.filter((col) => !isEmpty(col));
};
